import React, { useState, useEffect, useCallback } from 'react';
import { Grid, Button, Message, Modal } from 'semantic-ui-react';
import ManagementHeader from '../../../common/components/ManagementHeader';
import { useSharedData } from '../../hooks/core/useZoneHooks';
import { NestedLitGroupsBooksListEnh } from '../lists';
import { BookFormEnh } from '../forms';

/**
 * BookManagement-bc component
 * Book Creator version of BookManagement with NestedLitGroupsBooksListEnh
 * Manages books with nested Literary Groups/Books list for landing page use
 * @returns {JSX.Element} BookManagement-bc component
 */
const BookManagementBc = () => {
  const sharedData = useSharedData();

  // Extract data from new hook
  const {
    books,
    selections
  } = sharedData;

  // Map to old interface for compatibility
  const booksArray = Array.isArray(books) ? books : [];
  const selectionsObj = selections || {};

  const selectedBook = booksArray.find(b => b.uuid === selectionsObj.bookId) || null;
  const litGroupId = selectionsObj.litGroupId;
  const bookId = selectionsObj.bookId;

  // Map actions
  const setBookSelection = sharedData.actions.setBookSelection;
  const setLitGroupSelection = sharedData.actions.setLitGroupSelection;
  const fetchBooks = sharedData.actions.fetchBooks;

  // Wrap clearBookSelection in useCallback
  const clearBookSelection = useCallback(() => setBookSelection(null), [setBookSelection]);

  // State for editing mode
  const [isEditing, setIsEditing] = useState(false);

  // State for delete confirmation modals
  const [deleteBookModalOpen, setDeleteBookModalOpen] = useState(false);
  const [bookToDelete, setBookToDelete] = useState(null);

  // State for messages
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState(''); // eslint-disable-line no-unused-vars


  // Set editing mode when a book is selected
  useEffect(() => {
    setIsEditing(!!bookId);
  }, [bookId]);

  // Handler for selecting a book
  const handleBookSelect = useCallback((book) => {
    if (book && book.uuid) {
      // Set the selection first
      setBookSelection(book.uuid);
      setIsEditing(true);
    }
  }, [setBookSelection]);

  // Handler for confirming book deletion
  const confirmDeleteBook = useCallback(() => {
    // TODO: Implement book deletion API call
    console.log('Deleting book:', bookToDelete);

    // Close the modal
    setDeleteBookModalOpen(false);
    setBookToDelete(null);

    // If the deleted book was selected, clear the selection
    if (selectedBook && selectedBook.uuid === bookToDelete.uuid) {
      clearBookSelection();
      setIsEditing(false);
    }

    // Refresh the books list
    if (litGroupId) {
      fetchBooks(litGroupId);
    }
  }, [bookToDelete, selectedBook, clearBookSelection, litGroupId, fetchBooks]);

  // Handler for new book
  const handleNewBook = useCallback((group) => {
    // Set the literary group context
    setLitGroupSelection(group.uuid);

    // Clear book selection to show blank form
    setBookSelection(null);

    // Set editing mode
    setIsEditing(true);

    console.log('Creating new book for group:', group.name);
  }, [setLitGroupSelection, setBookSelection]);

  // Handle save
  const handleSaveBook = useCallback((updatedBook) => {
    // Refresh data after save
    if (updatedBook && updatedBook.uuid) {
      // Set the selection first
      setBookSelection(updatedBook.uuid);
      setIsEditing(true);

      // Refresh the books list
      if (litGroupId) {
        fetchBooks(litGroupId);
      }

      setSuccessMessage('Book saved successfully');
      setTimeout(() => setSuccessMessage(''), 3000);
    }
  }, [setBookSelection, litGroupId, fetchBooks]);

  return (
    <ManagementHeader
      title="Book Management"
      error={errorMessage}
      isLoading={false}
      width="1024px"
      isEditing={isEditing}
    >
      <Grid>
        <Grid.Row>
          <Grid.Column width={6}>
            <NestedLitGroupsBooksListEnh
              onBookSelect={handleBookSelect}
              onNewBook={handleNewBook}
            />
          </Grid.Column>

          <Grid.Column width={10}>
            {successMessage && (
              <Message positive>
                <Message.Header>Success</Message.Header>
                <p>{successMessage}</p>
              </Message>
            )}
            {/* 
            <Header as="h3" dividing>
              Book Details
              {selectedBook && selectedBook.name && (
                <span className="selectedItemTitle"> - {selectedBook.name}</span>
              )}
              {!selectedBook && litGroupId && (
                <span className="selectedItemTitle"> - New Book</span>
              )}
            </Header> */}

            {!litGroupId && !isEditing ? (
              <div className="bw-empty-selection-message">
                <p>Select a literary group from the list to view its books, or select a book to edit its details.</p>
              </div>
            ) : (
              <BookFormEnh onSave={handleSaveBook} />
            )}
          </Grid.Column>
        </Grid.Row>
      </Grid>

      {/* Delete Book Confirmation Modal */}
      <Modal open={deleteBookModalOpen} size="small">
        <Modal.Header>Delete Book</Modal.Header>
        <Modal.Content>
          <p>Are you sure you want to delete this book? This will also delete all characters associated with it. This action cannot be undone.</p>
          {bookToDelete && (
            <Message warning>
              <Message.Header>You are about to delete:</Message.Header>
              <p><strong>{bookToDelete.name}</strong></p>
            </Message>
          )}
        </Modal.Content>
        <Modal.Actions>
          <Button negative onClick={confirmDeleteBook}>
            Delete
          </Button>
          <Button onClick={() => setDeleteBookModalOpen(false)}>
            Cancel
          </Button>
        </Modal.Actions>
      </Modal>
    </ManagementHeader>
  );
};

export default BookManagementBc;
